import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { toast } from "sonner";
import { createSdk } from "~/lib/gql";
import { createWsClient } from "~/lib/gql-ws";
import { getSignalConfig, isActionable } from "~/pages/notifications/notifications.config";
import type { Signal } from "~/pages/notifications/notifications.types";

// ── Context shape ─────────────────────────────────────────────────────────────

interface NotificationsContextValue {
  signals: Signal[];
  unreadCount: number;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  handleMarkRead: (id: string) => void;
  handleMarkAllRead: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be inside NotificationsProvider");
  return ctx;
}

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * Runs the MyNotifications query, MarkRead/MarkAllRead mutations, and the
 * notificationReceived subscription once at the authenticated layout level.
 * Both NotificationsTray (header) and NotificationsPage consume from here —
 * no duplicate network connections.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? "";
  const sdk = createSdk(token);
  const queryClient = useQueryClient();

  // ── Query ─────────────────────────────────────────────────────────────────

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => sdk.MyNotifications({ limit: 50 }),
    enabled: !!token,
    staleTime: 30 * 1000,
  });

  const [signals, setSignals] = useState<Signal[]>([]);

  useEffect(() => {
    if (!data) return;
    setSignals(data.myNotifications.map((n) => ({ ...n, actionable: isActionable(n.type) })));
  }, [data]);

  // ── Cross-tab sync via BroadcastChannel ─────────────────────────────────
  //
  // When any tab marks notifications as read, it broadcasts a REFETCH event.
  // Other tabs that receive it invalidate their query — keeping them in sync.
  // BroadcastChannel does NOT fire on the originating tab, so there's no
  // double-invalidation: the mutating tab's own onSettled handles itself.

  const broadcastChannel = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    const channel = new BroadcastChannel("bowfast:notifications");
    broadcastChannel.current = channel;

    channel.onmessage = (event: MessageEvent<{ type: string }>) => {
      if (event.data?.type === "REFETCH") {
        void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
    };

    return () => {
      channel.close();
      broadcastChannel.current = null;
    };
  }, [queryClient]);

  function broadcastRefetch() {
    broadcastChannel.current?.postMessage({ type: "REFETCH" });
  }

  // ── Mutations ─────────────────────────────────────────────────────────────

  const markReadMutation = useMutation({
    mutationFn: (id: string) => sdk.MarkNotificationRead({ id }),
    onMutate: (id) => {
      setSignals((prev) => prev.map((s) => (s.id === id ? { ...s, readAt: new Date().toISOString() } : s)));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      broadcastRefetch();
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => sdk.MarkAllRead(),
    onMutate: () => {
      setSignals((prev) => prev.map((s) => (s.readAt ? s : { ...s, readAt: new Date().toISOString() })));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      broadcastRefetch();
    },
  });

  // ── Subscription ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;

    const client = createWsClient(token);

    const unsubscribe = client.subscribe(
      {
        query: `subscription NotificationReceived {
  notificationReceived {
    id
    type
    title
    body
    readAt
    createdAt
  }
}`,
      },
      {
        next: (event) => {
          if (!event.data) return;
          const raw = event.data.notificationReceived as {
            id: string;
            type: string;
            title: string;
            body: string;
            readAt?: string | null;
            createdAt: string;
          };
          const newSignal: Signal = { ...raw, actionable: isActionable(raw.type) };
          setSignals((prev) => [newSignal, ...prev]);

          const cfg = getSignalConfig(raw.type);
          const toastFn = cfg.level === "critical" ? toast.error : cfg.level === "urgent" ? toast.warning : toast;
          toastFn(raw.title, {
            description: raw.body.length > 80 ? `${raw.body.slice(0, 77)}…` : raw.body,
          });
        },
        error: () => {
          // Silently degrade — polling via staleTime provides eventual consistency.
        },
        complete: () => {},
      },
    );

    return () => {
      unsubscribe();
      void client.dispose();
    };
  }, [token]);

  // ── Value ─────────────────────────────────────────────────────────────────

  const unreadCount = signals.filter((s) => !s.readAt).length;

  return (
    <NotificationsContext.Provider
      value={{
        signals,
        unreadCount,
        isPending,
        isError,
        refetch: () => void refetch(),
        handleMarkRead: (id) => markReadMutation.mutate(id),
        handleMarkAllRead: () => markAllReadMutation.mutate(),
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}
